import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../../../firebase';
import { collection, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../../../hooks/useAuth';
import { RMS_PROVIDER_ROLES } from '../../../constants/userTypes';
import { RefreshIcon, UserAddIcon } from '@heroicons/react/outline';
import RmsSencoRegistrationForm from '../../components/provider/RmsSencoRegistrationForm';
import RmsSchoolUserManageDialog from '../../components/provider/RmsSchoolUserManageDialog';
import RmsTopNavBar from '../../components/provider/RmsTopNavBar';
import RmsSchoolTeachersPanel from '../../components/provider/RmsSchoolTeachersPanel';

/**
 * RmsSchoolUsersDashboard
 * -----------------------
 * Purpose:
 *   School-user management page for RMS providers. Lists the organisation's
 *   school users (SENCOs and other school representatives) and lets Org.
 *   Admins register new SENCOs with password-based accounts.
 *
 * Data:
 *   - Reads from the org-scoped subcollection:
 *     rms_organisations/{orgId}/schoolUsers
 *   - That collection also holds uid-keyed form-preference docs written by
 *     the referral flow; those carry no email field and are filtered out.
 *
 * Authorization:
 *   - Route access matches the Staff page (RMS Org. Admin and RMS Support)
 *   - Only RMS Org. Admin users can register new SENCOs (the backend
 *     callable rmsRegisterSchoolUser enforces the same gate)
 *
 * Teachers (TDI-621): below the school users, every school's teacher list
 * (RmsSchoolTeachersPanel), with the same gate: Org Admins change records,
 * Support Users view them.
 */

// Visual treatment per profile status: colour + label (meaning never rides on
// colour alone; the label always accompanies it).
const STATUS_BADGES = {
  invited: { label: 'Invited', className: 'bg-amber-100 text-amber-800' },
  active: { label: 'Active', className: 'bg-green-100 text-green-800' },
  deactivated: { label: 'Deactivated', className: 'bg-gray-100 text-gray-600' },
};

const PHASE_LABELS = {
  primary: 'Primary',
  secondary: 'Secondary',
};

export default function RmsSchoolUsersDashboard() {
  const { claims } = useAuth();
  const orgId = claims?.rmsOrgId;

  const [schoolUsers, setSchoolUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  // The account whose "Manage" dialog is open (TDI-772).
  const [manageUser, setManageUser] = useState(null);

  // Only RMS Org. Admin can register SENCOs; RMS Support reaches this page
  // read-only (mirrors the staff management gate).
  const canManageSchoolUsers = claims?.userType === RMS_PROVIDER_ROLES.ORG_ADMIN;

  useEffect(() => {
    if (!orgId) return;

    const schoolUsersRef = collection(db, 'rms_organisations', orgId, 'schoolUsers');

    const unsubscribe = onSnapshot(
      schoolUsersRef,
      (snapshot) => {
        const users = snapshot.docs
          .map((doc) => ({ schoolUserId: doc.id, ...doc.data() }))
          // Exclude the uid-keyed form-preference docs (no email field).
          .filter((user) => !!user.email);
        setSchoolUsers(users);
        setLoading(false);
      },
      (err) => {
        console.error('[RmsSchoolUsersDashboard] Error loading school users:', err);
        setError('Failed to load school users');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [orgId]);

  // One row per school account (TDI-772, R-042): the same person acquires a
  // profile per referral plus the registration's, so the list used to show
  // one SENCO up to fourteen times. Group by accountId and show the richest
  // profile (the registration's, where there is one); profiles with no
  // account stand alone.
  const groupedSchoolUsers = useMemo(() => {
    const richness = (p) => (p.source === 'provider_registration' ? 4 : 0)
      + (p.schoolName ? 2 : 0)
      + (p.firstName && p.lastName ? 1 : 0);
    const groups = new Map();
    schoolUsers.forEach((user) => {
      const key = user.accountId || `profile-${user.schoolUserId}`;
      groups.set(key, [...(groups.get(key) || []), user]);
    });
    return [...groups.values()].map((profiles) => {
      const best = [...profiles].sort((a, b) => (richness(b) - richness(a))
        || (b.createdAtIso || '').localeCompare(a.createdAtIso || ''))[0];
      // A deactivation marks every profile, but legacy rows can disagree:
      // deactivated anywhere means the sign-in is disabled, so the row says so.
      const status = profiles.some((p) => (p.status || '').toLowerCase() === 'deactivated')
        ? 'deactivated'
        : best.status;
      return { ...best, status };
    });
  }, [schoolUsers]);

  // Newest registrations first; falls back gracefully for docs without the
  // ISO timestamp (older, referral-created profiles).
  const sortedSchoolUsers = useMemo(
    () =>
      [...groupedSchoolUsers].sort((a, b) =>
        (b.createdAtIso || '').localeCompare(a.createdAtIso || '')
      ),
    [groupedSchoolUsers]
  );

  const breadcrumbs = [
    { label: 'Home', path: '/rms/provider/dashboard' },
    { label: 'Schools', path: null },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-white to-secondary/15 flex flex-col">
      <RmsTopNavBar breadcrumbs={breadcrumbs} />

      <div className="flex-1 px-6 sm:px-8 lg:px-12 py-8">
        {/* Header */}
        <div className="mb-8 text-left">
          <h1 className="text-2xl font-bold text-gray-900">School users</h1>
          <p className="text-sm text-gray-600 mt-1">
            View school users and register SENCOs for your organisation
          </p>
        </div>

        {/* Actions bar */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              <span className="font-medium">{sortedSchoolUsers.length}</span> school account
              {sortedSchoolUsers.length !== 1 ? 's' : ''} in your organisation
            </div>
            {canManageSchoolUsers && (
              <button
                onClick={() => setShowRegistrationForm(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
              >
                <UserAddIcon className="w-4 h-4" />
                Register SENCO
              </button>
            )}
          </div>
        </div>

        {/* Loading state */}
        {loading && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <RefreshIcon className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-3" />
            <p className="text-gray-600">Loading school users...</p>
          </div>
        )}

        {/* Error state */}
        {error && !loading && (
          <div className="bg-red-50 rounded-xl border border-red-200 p-6 text-center">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && sortedSchoolUsers.length === 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <p className="text-gray-900 font-medium mb-1">No school users yet</p>
            <p className="text-sm text-gray-600">
              {canManageSchoolUsers
                ? 'Register a SENCO to give a school access to referral forms.'
                : 'School users appear here once they are registered or complete a referral.'}
            </p>
          </div>
        )}

        {/* School users table */}
        {!loading && !error && sortedSchoolUsers.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    School
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Phase
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  {canManageSchoolUsers && (
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <span className="sr-only">Manage</span>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sortedSchoolUsers.map((user) => {
                  const badge = STATUS_BADGES[user.status] || {
                    label: user.status || 'Unknown',
                    className: 'bg-gray-100 text-gray-600',
                  };
                  return (
                    <tr key={user.schoolUserId}>
                      <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                        {user.displayName ||
                          [user.firstName, user.lastName].filter(Boolean).join(' ') ||
                          '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                        {user.email}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {user.schoolName || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                        {PHASE_LABELS[user.schoolPhase] || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.className}`}
                        >
                          {badge.label}
                        </span>
                      </td>
                      {canManageSchoolUsers && (
                        <td className="px-4 py-3 whitespace-nowrap">
                          {/* TDI-772: resend the set-up email, deactivate and
                              bring back, Org Admins only. A profile with no
                              signed-in account has nothing to manage. */}
                          {user.accountId ? (
                            <button
                              type="button"
                              onClick={() => setManageUser(user)}
                              className="text-sm font-semibold text-primary underline min-h-[44px] px-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                              aria-label={`Manage ${user.displayName || [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email}`}
                            >
                              Manage
                            </button>
                          ) : null}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Teachers on every school's list (TDI-621): Org Admins fix the
            records, Support Users view them. */}
        {orgId && <RmsSchoolTeachersPanel orgId={orgId} />}

        {/* Manage dialog (TDI-772): the live subscription shows the new
            status once an action lands. */}
        <RmsSchoolUserManageDialog
          orgId={orgId}
          user={manageUser}
          isOpen={manageUser !== null}
          onClose={() => setManageUser(null)}
        />

        {/* Registration dialog */}
        <RmsSencoRegistrationForm
          isOpen={showRegistrationForm}
          onClose={() => setShowRegistrationForm(false)}
          orgId={orgId}
          onSuccess={() => {
            // The real-time subscription will automatically update the table
          }}
        />
      </div>
    </div>
  );
}
